import { StarFilled } from "@ant-design/icons";
import { Skeleton, Typography } from "antd";
import { ReactNode, useId, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import ConfigureLink from "../../components/HelpCentre/ConfigureLink";
import HelpPanel from "../../components/HelpCentre/HelpPanel";
import HelpSearchBox from "../../components/HelpCentre/HelpSearchBox";
import HelpState from "../../components/HelpCentre/HelpState";
import SectionContent from "../../components/HelpCentre/SectionContent";
import { useHelpSearchIndex, useReaderTopics } from "../../hooks/useHelpCentre";
import { ReaderSection, ReaderTopic } from "../../types/helpCentre.types";
import {
	FAQ_PARAM,
	helpConfigurationPath,
	helpTopicPath,
	helpTopicsPath,
	TAB_PARAM,
	tabIndexFromParam,
} from "../../utils/helpCentre/content";
import { matchingSectionKeys, queryTerms } from "../../utils/helpCentre/search";

const sectionCount = (count: number) =>
	`${count} ${count === 1 ? "section" : "sections"}`;

/** Said in place of a topic or section that is hidden, gone, or for a module the tenant lacks. */
function Unavailable({ what }: Readonly<{ what: "topic" | "section" }>) {
	return (
		<div className="help-unavailable">
			<p>
				This {what} isn&apos;t available. It may have been moved or removed
				{what === "section" ? "; choose another section from the list." : "."}
			</p>
			{what === "topic" && <Link to={helpTopicsPath()}>Browse all topics</Link>}
		</div>
	);
}

/** The section list, narrowed by the hero's search, with the shown section marked. */
function SectionNav({
	topic,
	sections,
	activeKey,
	isFiltered,
}: Readonly<{
	topic: ReaderTopic;
	sections: ReaderSection[];
	activeKey?: string;
	isFiltered: boolean;
}>) {
	return (
		<nav aria-label="Sections">
			{sections.length ? (
				<ul className="help-topic__nav">
					{sections.map((section) => {
						const isActive = section.key === activeKey;
						return (
							<li key={section.key}>
								<Link
									to={helpTopicPath(topic.key, section.key)}
									className={`help-topic__nav-item${isActive ? " is-active" : ""}`}
									aria-current={isActive ? "page" : undefined}
								>
									{section.isImportant && (
										<>
											<StarFilled
												aria-hidden="true"
												className="help-topic__nav-star"
											/>
											<span className="sr-only">Featured: </span>
										</>
									)}
									<Typography.Text
										className="help-topic__nav-text"
										ellipsis={{
											tooltip: { title: section.title, placement: "right" },
										}}
									>
										{section.title}
									</Typography.Text>
								</Link>
							</li>
						);
					})}
				</ul>
			) : (
				<p className="help-empty">
					{isFiltered ? "No sections match." : "No sections yet."}
				</p>
			)}
		</nav>
	);
}

/**
 * One topic: its sections on the left and the chosen one on the right. The
 * URL carries the topic and section keys, plus `?tab=` for a tabbed article's
 * tab and `?faq=` for a question to open.
 */
function TopicPage({
	topicKey,
	sectionKey,
}: Readonly<{
	topicKey: string;
	sectionKey?: string;
}>) {
	const { topics, isError, isPending, isFetching, refetch } = useReaderTopics();
	const [searchParams, setSearchParams] = useSearchParams();
	const [filter, setFilter] = useState("");
	const sectionTitleId = useId();

	const topic = topics?.find((item) => item.key === topicKey);
	const topicList = useMemo(() => (topic ? [topic] : undefined), [topic]);
	const index = useHelpSearchIndex(topicList);
	const terms = useMemo(() => queryTerms(filter), [filter]);
	const isFiltered = terms.length > 0;

	const sections = useMemo(() => {
		if (!topic || !isFiltered) return topic?.children ?? [];
		const keys = matchingSectionKeys(index, terms, topic.key);
		return topic.children.filter((section) => keys.has(section.key));
	}, [topic, index, terms, isFiltered]);

	if (isError) {
		return (
			<div className="help-body">
				<HelpState
					onRetry={() => refetch()}
					retrying={isFetching}
					message="We couldn't load this topic."
				/>
			</div>
		);
	}

	if (isPending) {
		return (
			<>
				<div className="help-topic-hero" aria-busy="true">
					<Skeleton.Input active />
				</div>
				<div className="help-body help-body--wide">
					<div className="help-topic__split">
						<Skeleton active title={false} paragraph={{ rows: 5 }} />
						<Skeleton active paragraph={{ rows: 8 }} />
					</div>
				</div>
			</>
		);
	}

	if (!topic) {
		return (
			<>
				<div className="help-topic-hero">
					<h1 className="help-topic-hero__title">Topic not available</h1>
				</div>
				<div className="help-body">
					<Unavailable what="topic" />
				</div>
			</>
		);
	}

	// Without a section in the URL, the first one shows.
	const section = sectionKey
		? topic.children.find((item) => item.key === sectionKey)
		: topic.children[0];

	const tabCount =
		section?.type === "markdown" ? section.markdown.markdownTabs.length : 0;
	const changeTab = (tab: number) =>
		setSearchParams(
			(params) => {
				const next = new URLSearchParams(params);
				next.set(TAB_PARAM, String(tab + 1));
				return next;
			},
			{ replace: true }
		);

	let content: ReactNode;
	if (section) {
		content = (
			<>
				<h2 id={sectionTitleId} className="help-topic__section-title">
					{section.title}
				</h2>
				{/* Keyed so each section starts with its own tab and open questions. */}
				<SectionContent
					key={section.key}
					section={section}
					activeTab={tabIndexFromParam(searchParams.get(TAB_PARAM), tabCount)}
					onTabChange={changeTab}
					openFaqKey={searchParams.get(FAQ_PARAM)}
				/>
			</>
		);
	} else if (sectionKey) {
		content = <Unavailable what="section" />;
	} else {
		content = <p className="help-empty">This topic has no guides yet.</p>;
	}

	return (
		<>
			<div className="help-topic-hero">
				<div className="help-topic-hero__inner">
					<h1 className="help-topic-hero__title">{topic.topicTitle}</h1>
					<div className="help-page-header__actions">
						<HelpSearchBox
							value={filter}
							onChange={setFilter}
							label="Search sections"
							placeholder="Search sections"
						/>
						<ConfigureLink
							label="Edit this topic"
							to={helpConfigurationPath(topic.key, section?.key)}
						/>
					</div>
				</div>
			</div>

			<div className="help-body help-body--wide">
				<div className="help-topic__split">
					<HelpPanel title="Sections" className="help-topic__nav-panel">
						<SectionNav
							topic={topic}
							sections={sections}
							activeKey={section?.key}
							isFiltered={isFiltered}
						/>
					</HelpPanel>
					<section
						className="help-panel help-topic__content"
						aria-labelledby={section ? sectionTitleId : undefined}
						aria-label={section ? undefined : topic.topicTitle}
					>
						{content}
					</section>
				</div>
			</div>

			<output className="sr-only">
				{isFiltered ? `${sectionCount(sections.length)} found` : ""}
			</output>
		</>
	);
}

/** Keyed by topic, so moving to another topic starts its section search afresh. */
function HelpCentreTopic() {
	const { topicKey = "", sectionKey } = useParams();
	return (
		<TopicPage key={topicKey} topicKey={topicKey} sectionKey={sectionKey} />
	);
}

export default HelpCentreTopic;
